import { useReducer, Reducer } from "react";
import {
	BatchMessageSchedulingDetailState,
	ILanguage,
} from "../common/interfaces";
import Shared, { assertUnreachable } from "../common/shared";
import BatchMessageCostRequestModel from "../models/BatchMessage/BatchMessageCostRequestModel";
import BatchMessageDetailModel from "../models/BatchMessage/BatchMessageDetailModel";
import { GroupDetail } from "../models/BatchMessage/BatchMessageGroupDetailModel";
import { LanguageModel } from "../models/BatchMessage/BatchMessageLanguageDetailModel";
import BatchMessageSchedulingDetailModel, {
	MessageDetail,
	RecurrenceDetail,
} from "../models/BatchMessage/BatchMessageSchedulingDetailModel";

type Action =
	| {
			type: "INITIALIZE_BATCH_MESSAGE_SCHEDULE_DETAIL";
			payload: { batchNotification: BatchMessageDetailModel | null };
	  }
	| {
			type: "MESSAGE_SELECTION_CHANGE";
			payload: {
				isChecked: boolean;
				groupIndex?: number;
				messageIndex: number;
			};
	  }
	| {
			type: "LANGUAGE_SELECTION_CHANGE";
			payload: {
				values: ILanguage[] | null | undefined;
				groupIndex?: number;
				messageIndex: number;
				languages: LanguageModel[] | null | undefined;
			};
	  }
	| {
			type: "DATE_TIME_SELECTION_CHANGE";
			payload: {
				value: Date | string;
				groupIndex?: number;
				messageIndex: number;
			};
	  }
	| {
			type: "TIME_ZONE_SELECTION_CHANGE";
			payload: {
				value: string;
				groupIndex?: number;
				messageIndex: number;
			};
	  }
	| {
			type: "RECURRENCE_DETAIL_CHANGE";
			payload: {
				isRecurring: boolean;
				recurrenceDetails: RecurrenceDetail | undefined;
				groupIndex?: number;
				messageIndex: number;
			};
	  }
	| {
			type: "SET_MESSAGE_COST_DETAIL";
			payload: { messageCost: BatchMessageCostRequestModel };
	  };

function getLanguageIdByCodeAndId(
	id: string,
	code: string,
	languages: LanguageModel[]
): string {
	const temp = languages.find((x) => x.code === code && x.messageTitle === id);
	if (temp) {
		return temp.id;
	}
	return "";
}

export function GetMessageContent(
	batchMessageSchedulingDetails: BatchMessageSchedulingDetailModel[],
	languages: LanguageModel[],
	getIsBatchMessageTypeSMS: boolean,
	messageIndex: number,
	groupIndex: number = -1
) {
	if (batchMessageSchedulingDetails && languages.map((m) => m.message)) {
		const isMessageTypeVoice = !getIsBatchMessageTypeSMS;
		const messageDetailList: LanguageModel[] = languages;
		const messageList: string[] = [];
		let audioDurationLength: number = 0;
		const groupMessageDetail =
			batchMessageSchedulingDetails[groupIndex].messages[messageIndex];
		const messageDetail: LanguageModel[] = messageDetailList.filter(
			(m: LanguageModel) => groupMessageDetail.languageIds.includes(m.id)
		);
		if (messageDetail && messageDetail.length > 0) {
			messageDetail.forEach((lang: LanguageModel) => {
				const index = groupMessageDetail.languageIds.findIndex(
					(c: string) => c === lang.id
				);
				if (index > -1) {
					if (isMessageTypeVoice) {
						audioDurationLength = lang.audioDuration;
					} else {
						messageList.push(lang.message);
					}
				}
			});
		}
		return isMessageTypeVoice ? audioDurationLength : messageList.join("\n");
	}
	return "";
}

export function ValidateMessageScheduleRecord(messages: MessageDetail[]) {
	if (messages.length === 0) {
		return false;
	}
	for (let index = 0; index < messages.length; index++) {
		const messageDetail = messages[index];

		if (
			messageDetail.isSelected &&
			(messageDetail.languageIds.length === 0 ||
				messageDetail.timezone === "" ||
				(messageDetail.dateTime &&
					Shared.isBeforeCurrentDateTime(messageDetail.dateTime, new Date())))
		) {
			return false;
		}
	}
	return true;
}

export function IsMessageSelected(messageDetail: MessageDetail[]): boolean {
	for (
		let messageIndex = 0;
		messageIndex < messageDetail.length;
		messageIndex++
	) {
		if (messageDetail[messageIndex].isSelected) {
			return true;
		}
	}
	return false;
}

export const useBatchMessageScheduleConfiguration = () => {
	return useReducer<
		Reducer<BatchMessageSchedulingDetailState, Action>,
		undefined
	>(
		(state, action): BatchMessageSchedulingDetailState => {
			//reducer needs to return new state based on previous state and action parsed in
			switch (action.type) {
				case "INITIALIZE_BATCH_MESSAGE_SCHEDULE_DETAIL":
					const batchMessageSchedulingDetailList: BatchMessageSchedulingDetailModel[] = [];
					const batchNotificationTemp: BatchMessageDetailModel | null =
						action.payload.batchNotification;
					let messageTitleList: string[] = [];
					if (
						batchNotificationTemp &&
						batchNotificationTemp.batchMessageLanguageDetail?.languages
					) {
						messageTitleList = batchNotificationTemp.batchMessageLanguageDetail
							?.languages
							? batchNotificationTemp.batchMessageLanguageDetail?.languages.map(
									(x) => x.messageTitle
							  )
							: [];
						messageTitleList = messageTitleList.filter(
							(v: string, i: number) => messageTitleList.indexOf(v) === i
						);
					}
					if (batchNotificationTemp) {
						batchNotificationTemp.batchMessageGroupDetail?.groupDetails.forEach(
							(groupDetail: GroupDetail) => {
								const batchMessageDetail: BatchMessageSchedulingDetailModel = new BatchMessageSchedulingDetailModel();
								const messageSchedulingDetail: MessageDetail[] | undefined = [];
								if (
									batchNotificationTemp.batchMessageSchedulingDetail &&
									batchNotificationTemp.batchMessageSchedulingDetail.messages
										.length > 0
								) {
									const messageDetailList:
										| MessageDetail[]
										| undefined = batchNotificationTemp.batchMessageSchedulingDetail.messages.filter(
										(messageDetail: MessageDetail) =>
											messageDetail.groupId === groupDetail.id
									);
									messageSchedulingDetail.push(...messageDetailList);
								}
								messageTitleList.forEach((message: string) => {
									let batchMessage: MessageDetail = new MessageDetail();
									const messageDetailRecord: MessageDetail | undefined =
										messageSchedulingDetail &&
										messageSchedulingDetail?.length > 0
											? messageSchedulingDetail!.find(
													(messageDetail) => messageDetail.title === message
											  )
											: undefined;
									if (messageDetailRecord !== undefined) {
										messageDetailRecord.isSelected = true;
										messageDetailRecord.dateTime = new Date(
											messageDetailRecord.dateTime!
										);
										batchMessage = messageDetailRecord;
									} else {
										batchMessage.title = message;
										batchMessage.dateTime = new Date(
											new Date().setMinutes(new Date().getMinutes() + 15)
										);
										batchMessage.groupId = groupDetail.id ? groupDetail.id : "";
									}
									batchMessageDetail.messages.push(batchMessage);
								});
								batchMessageSchedulingDetailList.push(batchMessageDetail);
							}
						);
					}
					return {
						...state,
						batchMessageSchedulingDetails: batchMessageSchedulingDetailList,
					};
				case "MESSAGE_SELECTION_CHANGE":
					const batchMessageSchedulingDetailsTemp = [
						...state.batchMessageSchedulingDetails,
					];
					batchMessageSchedulingDetailsTemp[
						action.payload.groupIndex!
					].messages[action.payload.messageIndex].isSelected =
						action.payload.isChecked;
					return {
						...state,
						batchMessageSchedulingDetails: batchMessageSchedulingDetailsTemp,
					};
				case "LANGUAGE_SELECTION_CHANGE":
					const batchMessageSchedulingDetailsTT = [
						...state.batchMessageSchedulingDetails,
					];
					const languageListId: string[] = [];
					if (action.payload.values && action.payload.values.length > 0) {
						action.payload.values.forEach((element: ILanguage) => {
							languageListId.push(
								getLanguageIdByCodeAndId(
									batchMessageSchedulingDetailsTT[action.payload.groupIndex!]
										.messages[action.payload.messageIndex].title,
									element.code,
									action.payload.languages &&
										action.payload.languages.length > 0
										? action.payload.languages
										: []
								)
							);
						});
					}
					batchMessageSchedulingDetailsTT[action.payload.groupIndex!].messages[
						action.payload.messageIndex
					].languageIds = languageListId;
					return {
						...state,
						batchMessageSchedulingDetails: batchMessageSchedulingDetailsTT,
					};
				case "DATE_TIME_SELECTION_CHANGE":
					const batchMessageSchedulingDetailT = [
						...state.batchMessageSchedulingDetails,
					];
					batchMessageSchedulingDetailT[action.payload.groupIndex!].messages[
						action.payload.messageIndex
					].dateTime = action.payload.value
						? new Date(action.payload.value)
						: "";
					return {
						...state,
						batchMessageSchedulingDetails: batchMessageSchedulingDetailT,
					};
				case "TIME_ZONE_SELECTION_CHANGE":
					const batchMessageSchedulingDetailsTempTwo = [
						...state.batchMessageSchedulingDetails,
					];
					batchMessageSchedulingDetailsTempTwo[
						action.payload.groupIndex!
					].messages[action.payload.messageIndex].timezone =
						action.payload.value;

					return {
						...state,
						batchMessageSchedulingDetails: batchMessageSchedulingDetailsTempTwo,
					};
				case "RECURRENCE_DETAIL_CHANGE":
					const batchMessageSchedulingDetailsTempThree = [
						...state.batchMessageSchedulingDetails,
					];
					batchMessageSchedulingDetailsTempThree[
						action.payload.groupIndex!
					].messages[action.payload.messageIndex].isRecurring =
						action.payload.isRecurring;
					batchMessageSchedulingDetailsTempThree[
						action.payload.groupIndex!
					].messages[action.payload.messageIndex].recurrenceDetails =
						action.payload.recurrenceDetails;

					return {
						...state,
						batchMessageSchedulingDetails: batchMessageSchedulingDetailsTempThree,
					};
				case "SET_MESSAGE_COST_DETAIL":
					return {
						...state,
						batchMessageCostDetail: action.payload.messageCost,
					};
			}
			assertUnreachable(action);
		},
		undefined,
		() => ({
			batchMessageSchedulingDetails: [],
			batchMessageCostDetail: new BatchMessageCostRequestModel(),
		})
	);
};
